import { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Info } from "lucide-react";
import { ExpectationsSection } from "./form-sections/expectations/ExpectationsSection";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels } from "@/i18n";
import { ContactSection } from "./form-sections/expectations/ContactSection";
import { EstimatedPrice } from "./form-sections/expectations/EstimatedPrice";
import { useAuth } from "@/hooks/useAuth";
import { useValuation } from "@/hooks/useValuation";
import { useNavigate } from "react-router-dom";
import AuthDialog from "@/components/auth/AuthDialog";
import PrivacyPolicyDialog from "@/components/footer/privacy_policy_dialog";
import { VehicleDetails } from "@/types/vehicleDetails";
import { trackBilvarderingConversion } from "@/lib/gtag";
import {
  allowsMarketingCookies,
  openCookieConsentSettings,
} from "@/lib/cookieConsent";

interface ExpectationsFormProps {
  vehicleInfo: Partial<VehicleDetails>;
  embedMode?: boolean;
}

export const ExpectationsForm = ({
  vehicleInfo,
  embedMode = false,
}: ExpectationsFormProps) => {
  const { user, isAuthenticated } = useAuth();
  const { language } = useLanguage();
  const vLabels = valuationLabels(language);
  const {
    createValuationAsync,
    createValuationPublicAsync,
    getRegistrationDateAsync,
    valuationData,
    valuationPublicData,
  } = useValuation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    expectedPrice: "",
    location: "",
    name:
      user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : "",
    phone: user?.phone || "",
    email: user?.email || "",
    consent: false,
  });

  const [isValuating, setIsValuating] = useState(false);
  const [currentStep, setCurrentStep] = useState(3);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [comment, setComment] = useState("");
  const [commentError, setCommentError] = useState<string | undefined>(
    undefined,
  );
  const [valuationError, setValuationError] = useState(false);
  const bilvarderingTrackedRef = useRef(false);

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name:
          user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : prev.name,
        email: user.email || prev.email,
      }));
    }
  }, [user]);

  const handleShowEstimate = async () => {
    if (!comment.trim()) {
      setCommentError(vLabels.expectations.commentRequired);
      return;
    }
    // Guest Ads conversion requires marketing cookies — re-prompt if denied
    if (!isAuthenticated && !allowsMarketingCookies()) {
      openCookieConsentSettings({
        edit: true,
        reason: "valuation_requires_marketing",
      });
      return;
    }
    setCommentError(undefined);
    setValuationError(false);
    setIsValuating(true);

    try {
      // Step 1: Get registration date if we have regNumber
      let registrationDate: string | null = null;
      if (vehicleInfo.regNumber) {
        try {
          registrationDate = await getRegistrationDateAsync(
            vehicleInfo.regNumber,
          );
        } catch (error) {
          // Continue even if registration date fails
        }
      }

      // Step 2: Build vehicle details with registrationDate
      const vehicleDetailsWithRegistration: VehicleDetails = {
        ...(vehicleInfo as VehicleDetails),
        registrationDate: registrationDate || undefined,
      };

      // Step 2.5: Transform frontend format to backend format
      // Backend expects horsePower (capital P, number) but frontend uses horsepower (lowercase, string)
      const transformedVehicleDetails: any = {
        ...vehicleDetailsWithRegistration,
        horsePower: parseInt(vehicleDetailsWithRegistration.horsepower, 10),
      };
      // Remove the frontend-only field
      delete transformedVehicleDetails.horsepower;

      // Step 3: Prepare the valuation request data
      let result;
      // Convert expectedPrice to number if provided

      if (isAuthenticated) {
        // Authenticated user - no contactInfo
        const valuationRequest = {
          vehicleDetails: transformedVehicleDetails,
          expectedPrice: formData.expectedPrice,
          location: formData.location,
          comment: comment,
        };
        result = await createValuationAsync(valuationRequest);
      } else {
        // Public user - with contactInfo
        const valuationRequest = {
          vehicleDetails: transformedVehicleDetails,
          expectedPrice: formData.expectedPrice,
          location: formData.location,
          comment: comment,
          contactInfo: {
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            consent: formData.consent,
          },
        };
        result = await createValuationPublicAsync(valuationRequest);
        // Guest Ads: fire when the public submit succeeds (lead created),
        // even if AutoUncle returns no price (UI shows soft-fail).
        if (!bilvarderingTrackedRef.current) {
          bilvarderingTrackedRef.current = true;
          trackBilvarderingConversion({
            car_brand: vehicleInfo.make,
            car_model: vehicleInfo.model,
          });
        }
      }

      // Check if result is empty or invalid
      if (!result || Object.keys(result).length === 0) {
        setValuationError(true);
        setIsValuating(false);
        setCurrentStep(4);
        return;
      }

      // Move to step 4 to show the estimate
      setCurrentStep(4);
    } catch (error) {
      setValuationError(true);
      setIsValuating(false);
      setCurrentStep(4);
    }
  };

  const buildCarPostAdUrl = () => {
    const searchParams = new URLSearchParams();

    // Basic vehicle info
    if (vehicleInfo.regNumber)
      searchParams.append("regNumber", vehicleInfo.regNumber);
    if (vehicleInfo.make) searchParams.append("make", vehicleInfo.make);
    if (vehicleInfo.model) searchParams.append("model", vehicleInfo.model);
    if (vehicleInfo.year)
      searchParams.append("year", vehicleInfo.year.toString());
    if (vehicleInfo.fuelType)
      searchParams.append("fuelType", vehicleInfo.fuelType);
    if (vehicleInfo.engine) searchParams.append("engine", vehicleInfo.engine);
    if (vehicleInfo.vehicleType)
      searchParams.append("vehicleType", vehicleInfo.vehicleType);

    // Additional vehicle details that might be available from CarValuation
    // These would come from the vehicleDetails state in CarValuationWidget
    // We need to pass them through the vehicleInfo prop
    if (vehicleInfo.bodyType)
      searchParams.append("bodyType", vehicleInfo.bodyType);
    if (vehicleInfo.color) searchParams.append("color", vehicleInfo.color);
    if (vehicleInfo.transmission)
      searchParams.append("transmission", vehicleInfo.transmission);
    if (vehicleInfo.trim) searchParams.append("trim", vehicleInfo.trim);
    if (vehicleInfo.condition)
      searchParams.append("condition", vehicleInfo.condition);
    if (vehicleInfo.mileage)
      searchParams.append(
        "mileage",
        typeof vehicleInfo.mileage === "number"
          ? vehicleInfo.mileage.toString()
          : vehicleInfo.mileage,
      );
    if (vehicleInfo.keys)
      searchParams.append("keys", vehicleInfo.keys.toString());
    if (vehicleInfo.currentTireType)
      searchParams.append("currentTireType", vehicleInfo.currentTireType);
    if (vehicleInfo.currentRimType)
      searchParams.append("currentRimType", vehicleInfo.currentRimType);
    if (vehicleInfo.extraTireType)
      searchParams.append("extraTireType", vehicleInfo.extraTireType);
    if (vehicleInfo.extraRimType)
      searchParams.append("extraRimType", vehicleInfo.extraRimType);
    if (vehicleInfo.vin) searchParams.append("vin", vehicleInfo.vin);
    if (vehicleInfo.registrationDate)
      searchParams.append("registrationDate", vehicleInfo.registrationDate);

    // Additional fields that should be passed from valuation
    if (vehicleInfo.variant)
      searchParams.append("variant", vehicleInfo.variant);
    if (vehicleInfo.horsepower)
      searchParams.append("horsepower", vehicleInfo.horsepower);
    if (vehicleInfo.engineVolume)
      searchParams.append("engineVolume", vehicleInfo.engineVolume);
    if (vehicleInfo.doors)
      searchParams.append("doors", vehicleInfo.doors.toString());
    if (vehicleInfo.electricDriveRange)
      searchParams.append(
        "electricDriveRange",
        vehicleInfo.electricDriveRange.toString(),
      );

    // Equipment array (JSON encoded)
    if (vehicleInfo.equipment && vehicleInfo.equipment.length > 0) {
      searchParams.append("equipment", JSON.stringify(vehicleInfo.equipment));
    }

    const queryString = searchParams.toString();
    return `/car-post-an-ad${queryString ? `?${queryString}` : ""}`;
  };

  const handleCreateAd = () => {
    if (isAuthenticated) {
      if (embedMode) {
        // In embed mode, open in a new tab (outside the iframe)
        // Use absolute URL to ensure proper navigation in new tab
        const url = buildCarPostAdUrl();
        const absoluteUrl = window.location.origin + url;
        window.open(absoluteUrl, "_blank");
      } else {
        // Normal mode, navigate within the app
        const url = buildCarPostAdUrl();
        navigate(url);
      }
    } else {
      if (embedMode) {
        // In embed mode, open /auth in a new tab with return URL
        const returnUrl = buildCarPostAdUrl();
        const authUrl = `/auth?returnUrl=${encodeURIComponent(returnUrl)}`;
        const absoluteAuthUrl = window.location.origin + authUrl;
        window.open(absoluteAuthUrl, "_blank");
      } else {
        // Show authentication dialog - user needs to be logged in!
        setShowAuthDialog(true);
      }
    }
  };

  const isContactValid = !!(
    isAuthenticated ||
    (formData.name && formData.consent && formData.phone && formData.email)
  );

  return (
    <div className="container max-w-2xl mx-auto py-4 sm:py-8 px-2 sm:px-4 space-y-4 sm:space-y-6">
      {currentStep === 3 && (
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              <ExpectationsSection
                expectedPrice={formData.expectedPrice}
                location={formData.location}
                comment={comment}
                onExpectedPriceChange={(value) =>
                  setFormData({ ...formData, expectedPrice: value })
                }
                onLocationChange={(value) =>
                  setFormData({ ...formData, location: value })
                }
                commentError={commentError}
                onCommentChange={(val) => {
                  setComment(val);
                  if (val.trim()) setCommentError(undefined);
                }}
                labels={vLabels.expectations}
              />

              {!isAuthenticated ? (
                <ContactSection
                  name={formData.name}
                  phone={formData.phone}
                  email={formData.email}
                  consent={formData.consent}
                  onNameChange={(value) =>
                    setFormData({ ...formData, name: value })
                  }
                  onPhoneChange={(value) =>
                    setFormData({ ...formData, phone: value })
                  }
                  onEmailChange={(value) =>
                    setFormData({ ...formData, email: value })
                  }
                  onConsentChange={(checked) =>
                    setFormData({ ...formData, consent: checked })
                  }
                  onOpenPrivacyDialog={() => setShowPrivacyDialog(true)}
                  labels={vLabels.contact}
                />
              ) : null}
              <div className="flex justify-end pt-4">
                <Button
                  type="button"
                  onClick={handleShowEstimate}
                  disabled={!isContactValid || !comment.trim() || isValuating}
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-2"
                >
                  {isValuating
                    ? vLabels.expectations.calculating
                    : vLabels.expectations.calculate}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {currentStep === 4 && (
        <Card>
          <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {vehicleInfo.vehicleType === "mc" ? (
              // MC-specific UI - AutoUncle cannot valuate motorcycles
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <Info className="h-8 w-8 sm:h-10 sm:w-10 text-green-600 dark:text-green-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-base sm:text-lg font-semibold text-green-900 dark:text-green-100">
                    {vLabels.expectations.thanksForValuation}
                  </h3>
                  {embedMode ? (
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 px-2 sm:px-4">
                      {vLabels.expectations.mcThanksEmbed}
                    </p>
                  ) : (
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 px-2 sm:px-4">
                      {vLabels.expectations.mcThanks}
                    </p>
                  )}
                </div>

                <div className="flex justify-center pt-4">
                  <Button
                    onClick={handleCreateAd}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-8 py-2 sm:py-3 text-sm sm:text-lg font-medium text-center whitespace-normal"
                  >
                    {vLabels.expectations.continueCreateAd}
                  </Button>
                </div>
              </div>
            ) : valuationError || (!valuationData && !valuationPublicData) ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <AlertTriangle className="h-8 w-8 sm:h-10 sm:w-10 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-base sm:text-lg font-semibold text-orange-900 dark:text-orange-100">
                    {vLabels.expectations.valuationErrorTitle}
                  </h3>
                  {embedMode ? (
                    <>
                      <p className="text-sm sm:text-base text-orange-800 dark:text-orange-200 px-2 sm:px-4">
                        {vLabels.expectations.valuationErrorDescEmbed}
                      </p>
                      <div className="flex justify-center pt-4">
                        <Button
                          onClick={handleCreateAd}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-8 py-2 sm:py-3 text-sm sm:text-lg font-medium text-center whitespace-normal"
                        >
                          {vLabels.expectations.continueCreateAd}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-sm sm:text-base text-orange-800 dark:text-orange-200 px-2 sm:px-4">
                        {vLabels.expectations.valuationErrorDesc}
                      </p>
                      <div className="pt-2">
                        <Info className="h-5 w-5 text-orange-600 dark:text-orange-400 mx-auto" />
                      </div>
                      <div className="flex justify-center pt-4">
                        <Button
                          onClick={handleCreateAd}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-8 py-2 sm:py-3 text-sm sm:text-lg font-medium text-center whitespace-normal"
                        >
                          {vLabels.expectations.continueCreateAd}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                <EstimatedPrice
                  vehicleInfo={vehicleInfo}
                  valuationData={valuationData || valuationPublicData}
                  labels={vLabels.expectations}
                />

                {embedMode ? (
                  <div className="text-center space-y-4 pt-4">
                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 px-2 sm:px-4">
                      {vLabels.expectations.happyWithValuation}
                    </p>
                    <div className="flex justify-center pt-2">
                      <Button
                        onClick={handleCreateAd}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-8 py-2 sm:py-3 text-sm sm:text-lg font-medium text-center whitespace-normal"
                      >
                        {vLabels.expectations.continueCreateAd}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center pt-4">
                    <Button
                      onClick={handleCreateAd}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 sm:px-8 py-2 sm:py-3 text-sm sm:text-lg font-medium text-center whitespace-normal"
                    >
                      {vLabels.expectations.wantToCreateAd}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Authentication Dialog */}
      <AuthDialog open={showAuthDialog} onOpenChange={setShowAuthDialog} />

      {/* Privacy Policy Dialog */}
      <PrivacyPolicyDialog
        open={showPrivacyDialog}
        onOpenChange={setShowPrivacyDialog}
      />
    </div>
  );
};
